import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Card,
  CardHeader,
  CardBody,
  Typography,
} from '@statsbomb/kitbag-components';
import {
  buildQuarterScores,
  getQuarterLabel,
} from './GameOverview.dataManipulation';
import StatGrid from '../../../components/Tables/StatGrid/StatGrid';
import LoaderLabelled from '../../../components/LoaderLabelled/LoaderLabelled';
import { OffenseCell } from './GameOverview.styles';
import TeamIcon from './TeamIcon';
import { ScoreboardWrap } from './Scoreboard.styles';

// Team cell: half-moon icon (matching the page header) + abbreviation / full
// name, rendered like the Play by Play table's Offense column. The abbrev ↔
// full swap is driven by the container query in Scoreboard.styles.
function TeamRowCell({ data, context }) {
  return (
    <OffenseCell>
      <TeamIcon team={data.team} context={context} />
      <span className="team-abbrev">{data.abbrev}</span>
      <span className="team-full">{data.team.name}</span>
    </OffenseCell>
  );
}

TeamRowCell.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  data: PropTypes.object.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  context: PropTypes.object.isRequired,
};

function Scoreboard({
  game,
  plays,
  loading,
  isHomeTeam,
  selectedTeamColor,
  opponentTeamColor,
}) {
  const quarterScores = useMemo(
    () => buildQuarterScores(plays, game),
    [plays, game],
  );
  const columnDefs = useMemo(
    // Every column flexes between a minimum and a maximum width, so the table
    // fits a narrow card without a horizontal scrollbar and hands any spare
    // width to the Team column.
    () => [
      {
        colId: 'team',
        headerName: 'Team',
        cellRenderer: TeamRowCell,
        flex: 1,
        minWidth: 64,
      },
      ...quarterScores.map(({ quarter }) => ({
        colId: `q${quarter}`,
        headerName: getQuarterLabel(quarter),
        type: 'rightAligned',
        valueGetter: ({ data }) => data.scores[quarter],
        flex: 1,
        minWidth: 36,
        maxWidth: 64,
      })),
      {
        colId: 'final',
        headerName: 'Final',
        type: 'rightAligned',
        valueGetter: ({ data }) => data.final,
        cellClass: 'scoreboard-final',
        flex: 1,
        minWidth: 44,
        maxWidth: 72,
      },
    ],
    [quarterScores],
  );

  if (!game?.homeTeam || !game?.awayTeam) {
    if (!loading) return null;
    // Reserve the card while the game loads so the right column does not
    // jump when the scoreboard appears.
    return (
      <ScoreboardWrap>
        <Card>
          <CardHeader>
            <Typography variant="headingMedium">Scoreboard</Typography>
          </CardHeader>
          <CardBody>
            <LoaderLabelled text="Loading scoreboard" height="8rem" />
          </CardBody>
        </Card>
      </ScoreboardWrap>
    );
  }

  const scoresFor = (key) =>
    Object.fromEntries(quarterScores.map((q) => [q.quarter, q[key]]));

  const awayRow = {
    team: game.awayTeam,
    abbrev: game.awayTeam.shortName || game.awayTeam.name,
    scores: scoresFor('away'),
    final: game.awayScore ?? 0,
    isHome: false,
  };
  const homeRow = {
    team: game.homeTeam,
    abbrev: game.homeTeam.shortName || game.homeTeam.name,
    scores: scoresFor('home'),
    final: game.homeScore ?? 0,
    isHome: true,
  };
  // Selected team first, so the order matches the page header.
  const rowData = isHomeTeam ? [homeRow, awayRow] : [awayRow, homeRow];

  return (
    <ScoreboardWrap>
      <Card>
        <CardHeader>
          <Typography variant="headingMedium">Scoreboard</Typography>
        </CardHeader>
        <CardBody>
          <StatGrid
            columnDefs={columnDefs}
            rowData={rowData}
            context={{
              selectedTeamId: isHomeTeam ? game.homeTeam.id : game.awayTeam.id,
              selectedTeamColor,
              opponentTeamColor,
            }}
          />
        </CardBody>
      </Card>
    </ScoreboardWrap>
  );
}

Scoreboard.propTypes = {
  game: PropTypes.shape({
    homeScore: PropTypes.number,
    awayScore: PropTypes.number,
    homeTeam: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      shortName: PropTypes.string,
      colorPrimary: PropTypes.string,
      colorSecondary: PropTypes.string,
    }),
    awayTeam: PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      shortName: PropTypes.string,
      colorPrimary: PropTypes.string,
      colorSecondary: PropTypes.string,
    }),
  }),
  // eslint-disable-next-line react/forbid-prop-types
  plays: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  isHomeTeam: PropTypes.bool,
  selectedTeamColor: PropTypes.string,
  opponentTeamColor: PropTypes.string,
};

Scoreboard.defaultProps = {
  game: null,
  plays: [],
  loading: false,
  isHomeTeam: true,
  selectedTeamColor: null,
  opponentTeamColor: null,
};

export default Scoreboard;
